import {
	ClassicyApp,
	ClassicyButton,
	ClassicyCheckbox,
	ClassicyControlGroup,
	ClassicyControlLabel,
	ClassicyIcons,
	ClassicyInput,
	ClassicyWindow,
	quitAppHelper,
	useAppManager,
	useAppManagerDispatch,
} from "classicy";
import { manifestDescription } from "../../Components/manifestDescription";
import DOMPurify from "dompurify";
import {
	type FC as FunctionalComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useAboutApp } from "../../Components/AboutApp/AboutApp";
import { trackAppToggle } from "../../openreplay";
import "./Browser.scss";
import "./BrowserContext";
import type {
	BrowserFavorite,
	BrowserHistoryEntry,
	BrowserHomePage,
	BrowserRemoteCommand,
} from "./BrowserContext";
import {
	buildLinkStyle,
	DEFAULT_VISITED_COLOR,
	extractLinkColors,
} from "./browserUtils";
import {
	DEFAULT_PROXY_CONFIG,
	type TimeMachineProxyConfig,
	useBrowserNavigation,
} from "./useBrowserNavigation";

const DEFAULT_FAVORITES: BrowserFavorite[] = [
	{
		id: "cnn",
		title: "CNN",
		url: "http://www.cnn.com/",
		icon: ClassicyIcons.applications.internetExplorer.news,
	},
];

interface ShadowLinkClick {
	href: string;
	rawHref: string;
}

export const ShadowContent: FunctionalComponent<{
	html: string;
	onLinkClick: (link: ShadowLinkClick) => void;
	isVisited?: (href: string, rawHref: string) => boolean;
}> = ({ html, onLinkClick, isVisited }) => {
	const hostRef = useRef<HTMLDivElement>(null);
	const shadowRef = useRef<ShadowRoot | null>(null);
	// Persistent siblings inside the shadow root: styleRef holds the page's
	// reconstructed link colors; docRef holds the sanitized page. Only docRef's
	// innerHTML is replaced per navigation, so the <style> survives.
	const styleRef = useRef<HTMLStyleElement | null>(null);
	const docRef = useRef<HTMLDivElement | null>(null);
	const onLinkClickRef = useRef(onLinkClick);
	onLinkClickRef.current = onLinkClick;

	useEffect(() => {
		if (hostRef.current && !shadowRef.current) {
			const shadow = hostRef.current.attachShadow({ mode: "open" });
			const style = document.createElement("style");
			const doc = document.createElement("div");
			shadow.append(style, doc);
			shadowRef.current = shadow;
			styleRef.current = style;
			docRef.current = doc;
		}
		// No cleanup: ShadowRoot cannot be detached once attached (browser limitation)
	}, []);

	useEffect(() => {
		if (!docRef.current || !styleRef.current) return;
		// Content is sanitized via DOMPurify before being set
		docRef.current.innerHTML = DOMPurify.sanitize(html, { FORCE_BODY: true });
		// Recreate the page's <body link/vlink/alink> colors as a stylesheet,
		// always giving visited links a color even when the page declares none.
		const colors = extractLinkColors(html);
		styleRef.current.textContent = buildLinkStyle({
			...colors,
			visited: colors.visited ?? DEFAULT_VISITED_COLOR,
		});
	}, [html]);

	useEffect(() => {
		const shadow = shadowRef.current;
		if (!shadow) return;
		const handler = (e: Event) => {
			const mouseEvent = e as MouseEvent;
			const clickTarget = mouseEvent.composedPath()[0] as
				| HTMLElement
				| undefined;
			if (!clickTarget) return;
			// `area` matches image-map regions: a click inside a <map>'s region is
			// dispatched on the <area>, which has .href/href like an <a> but isn't
			// one — without this it escapes to the host browser's native navigation.
			const link = clickTarget.closest?.("a, area") as
				| HTMLAnchorElement
				| HTMLAreaElement
				| null;
			if (!link) return;
			mouseEvent.preventDefault();
			onLinkClickRef.current({
				href: link.href,
				rawHref: link.getAttribute("href") || "",
			});
		};
		shadow.addEventListener("click", handler);
		return () => shadow.removeEventListener("click", handler);
	}, []);

	// Tag links the user has already visited with `.browserVisited`; the
	// stylesheet (set above) colors them. Re-runs when the page changes or the
	// visited set grows (isVisited identity changes).
	useEffect(() => {
		const doc = docRef.current;
		if (!doc || !isVisited) return;
		doc.querySelectorAll("a[href], area[href]").forEach((el) => {
			const link = el as HTMLAnchorElement | HTMLAreaElement;
			link.classList.toggle(
				"browserVisited",
				isVisited(link.href, link.getAttribute("href") || ""),
			);
		});
	}, [html, isVisited]);

	return <div ref={hostRef} className="browserPage" />;
};

const PROTOCOL_OPTIONS = [
	{ value: "http:", label: "http" },
	{ value: "https:", label: "https" },
	{ value: "ws:", label: "ws" },
	{ value: "wss:", label: "wss" },
];

const DEFAULT_URL = "http://www.apple.com/";
const DEFAULT_HOME_LABEL = "Apple";
const DEFAULT_HOME_ICON = ClassicyIcons.applications.internetExplorer.apple;

export const Browser = () => {
	const appName = "Browser";
	const appId = "Browser.app";
	const appIcon = ClassicyIcons.applications.internetExplorer.app;
	const aboutWindow = useAboutApp(appId, appIcon);

	const desktopEventDispatch = useAppManagerDispatch();
	const appState = useAppManager(
		(state) => state.System.Manager.Applications.apps[appId],
	);

	const isOpen = useAppManager(
		(state) =>
			state.System.Manager.Applications.apps[appId]?.open ?? false,
	);
	const prevIsOpenRef = useRef<boolean | undefined>(undefined);
	useEffect(() => {
		if (prevIsOpenRef.current === undefined) {
			prevIsOpenRef.current = isOpen;
			return;
		}
		if (prevIsOpenRef.current === isOpen) return;
		prevIsOpenRef.current = isOpen;
		trackAppToggle(appId, isOpen ? "open" : "close");
	}, [isOpen]);

	const favorites = useAppManager(
		(state) => (state.System.Manager.Applications.apps[appId]?.data?.favorites ?? []) as BrowserFavorite[],
	);

	const history = useAppManager(
		(state) => (state.System.Manager.Applications.apps[appId]?.data?.history ?? []) as BrowserHistoryEntry[],
	);

	const proxyConfig: TimeMachineProxyConfig =
		(appState?.data?.proxyConfig as TimeMachineProxyConfig | undefined) ??
		DEFAULT_PROXY_CONFIG;

	const normalizeDomain = useCallback((url: string): string => {
		try {
			const hostname = new URL(url).hostname;
			return hostname.startsWith("www.") ? hostname.slice(4) : hostname;
		} catch {
			return "";
		}
	}, []);

	// Memoized so the windowIcon useMemo below has a stable dependency; without
	// this the `?? {…}` fallback is a fresh object every render.
	const homePage: BrowserHomePage = useMemo(
		() =>
			(appState?.data?.homePage as BrowserHomePage | undefined) ?? {
				url: DEFAULT_URL,
				label: DEFAULT_HOME_LABEL,
				icon: DEFAULT_HOME_ICON,
			},
		[appState?.data?.homePage],
	);

	useEffect(() => {
		if (!appState) return;
		if (!appState.data?.favorites) {
			desktopEventDispatch({
				type: "ClassicyAppBrowserInitFavorites",
				favorites: DEFAULT_FAVORITES,
			});
		}
		if (!appState.data?.homePage) {
			desktopEventDispatch({
				type: "ClassicyAppBrowserSetHomePage",
				url: DEFAULT_URL,
				label: DEFAULT_HOME_LABEL,
				icon: DEFAULT_HOME_ICON,
			});
		}
	}, [appState, desktopEventDispatch]);

	const showFavoritesBar: boolean = (appState?.data?.showFavoritesBar as boolean) ?? true;
	const [urlError, setUrlError] = useState(false);
	const [showSettings, setShowSettings] = useState(false);
	const refToolbar = useRef<HTMLDivElement>(null);
	const [isCompact, setIsCompact] = useState(false);

	// Settings form state — initialized from persisted config
	const [settingsForm, setSettingsForm] =
		useState<TimeMachineProxyConfig>(proxyConfig);

	// Re-sync form when settings window opens
	const openSettings = useCallback(() => {
		setSettingsForm(proxyConfig);
		setShowSettings(true);
		desktopEventDispatch({
			type: "ClassicyWindowFocus",
			app: { id: appId },
			window: { id: "browser_settings" },
		});
	}, [proxyConfig, desktopEventDispatch]);

	const saveSettings = useCallback(() => {
		desktopEventDispatch({
			type: "ClassicyAppBrowserUpdateProxyConfig",
			proxyConfig: settingsForm,
		});
		setShowSettings(false);
	}, [settingsForm, desktopEventDispatch]);

	const showError = useCallback(() => {
		setUrlError(true);
		desktopEventDispatch({
			type: "ClassicyWindowFocus",
			app: { id: appId },
			window: { id: "browser_error" },
		});
	}, [desktopEventDispatch]);

	const recordVisit = useCallback(
		(url: string) => {
			desktopEventDispatch({
				type: "ClassicyAppBrowserRecordVisit",
				url,
			});
		},
		[desktopEventDispatch],
	);

	const {
		htmlContent,
		pageTitle,
		addressBarValue,
		setAddressBarValue,
		isLoading,
		statusText,
		canGoBack,
		canGoForward,
		goTo,
		goBack,
		goForward,
		handleContentClick,
		isVisited,
	} = useBrowserNavigation({
		defaultUrl: DEFAULT_URL,
		proxyConfig,
		onShowError: showError,
		onRecordVisit: recordVisit,
		visitedHistory: history,
	});

	// Apply each remote navigate command (playlist scheduled browser entries)
	// exactly once, tracked by its monotonic seq. No retry condition needed —
	// navigation depends on no stream data.
	const remoteCommand = appState?.data?.command as BrowserRemoteCommand | undefined;
	const lastCommandSeqRef = useRef(0);
	useEffect(() => {
		if (!remoteCommand || remoteCommand.seq <= lastCommandSeqRef.current) return;
		lastCommandSeqRef.current = remoteCommand.seq;
		if (remoteCommand.kind === "navigate") goTo(remoteCommand.url);
	}, [remoteCommand, goTo]);

	const windowIcon = useMemo(() => {
		const currentDomain = normalizeDomain(addressBarValue);
		if (!currentDomain) return appIcon;
		if (normalizeDomain(homePage.url) === currentDomain) return homePage.icon;
		const match = favorites.find(
			(f) => normalizeDomain(f.url) === currentDomain,
		);
		return match ? match.icon : appIcon;
	}, [addressBarValue, homePage, favorites, normalizeDomain, appIcon]);

	// Hide button labels when toolbar is narrow
	useEffect(() => {
		if (!refToolbar.current) return;
		const observer = new ResizeObserver(([entry]) => {
			setIsCompact(entry.contentRect.width < 450);
		});
		observer.observe(refToolbar.current);
		return () => observer.disconnect();
	}, []);

	const quitApp = useCallback(() => {
		desktopEventDispatch(quitAppHelper(appId, appName, appIcon));
	}, [desktopEventDispatch, appIcon]);

	const appMenu = useMemo(
		() => [
			{
				id: "file",
				title: "File",
				menuChildren: [
					{
						id: `${appId}_settings`,
						title: "Settings…",
						onClickFunc: openSettings,
					},
					{
						id: `${appId}_quit`,
						title: "Quit",
						onClickFunc: quitApp,
					},
				],
			},
			{
				id: "view",
				title: "View",
				menuChildren: [
					{
						id: `${appId}_show_favorites`,
						title: "Show Favorites",
						className: showFavoritesBar ? "browserMenuItemChecked" : "",
						onClickFunc: () => desktopEventDispatch({ type: "ClassicyAppBrowserSetShowFavoritesBar", showFavoritesBar: !showFavoritesBar }),
					},
				],
			},
		],
		[quitApp, openSettings, showFavoritesBar, desktopEventDispatch],
	);

	return (
		<ClassicyApp
			id={appId}
			name={appName}
			icon={appIcon}
			defaultWindow={"browser"}
			desktopIconBalloonHelp={manifestDescription(appId)}
		>
			{showSettings && (
				<ClassicyWindow
					id={"browser_settings"}
					title={"Settings"}
					appId={appId}
					closable={true}
					resizable={false}
					zoomable={false}
					scrollable={false}
					collapsable={false}
					initialSize={[350, 0]}
					initialPosition={[250, 150]}
					appMenu={appMenu}
					onCloseFunc={() => setShowSettings(false)}
				>
					<div className="browserSettings">
						<ClassicyControlGroup label="TimeMachine Proxy">
							<ClassicyCheckbox
								id="proxy_enabled"
								checked={settingsForm.enabled}
								label="Enable TimeMachine Proxy"
								onClickFunc={(checked) =>
									setSettingsForm((s) => ({ ...s, enabled: checked }))
								}
							/>
						</ClassicyControlGroup>
						<ClassicyControlGroup label="Connection" columns={true}>
							<div className="browserSettingsRow">
								<ClassicyControlLabel label="Protocol:" />
								<div className="browserSettingsProtocol">
									{PROTOCOL_OPTIONS.map((opt) => (
										<ClassicyButton
											key={opt.value}
											buttonSize="medium"
											isDefault={settingsForm.protocol === opt.value}
											disabled={!settingsForm.enabled}
											onClickFunc={() =>
												setSettingsForm((s) => ({
													...s,
													protocol: opt.value,
												}))
											}
										>
											{opt.label}
										</ClassicyButton>
									))}
								</div>
							</div>
							<ClassicyInput
								id="proxy_host"
								labelTitle="Host:"
								prefillValue={settingsForm.host}
								disabled={!settingsForm.enabled}
								onChangeFunc={(e) =>
									setSettingsForm((s) => ({
										...s,
										host: e.target.value,
									}))
								}
							/>
							<ClassicyInput
								id="proxy_port"
								labelTitle="Port:"
								prefillValue={String(settingsForm.port)}
								disabled={!settingsForm.enabled}
								onChangeFunc={(e) => {
									const val = parseInt(e.target.value, 10);
									if (!isNaN(val) && val > 0 && val <= 65535) {
										setSettingsForm((s) => ({ ...s, port: val }));
									}
								}}
							/>
							<ClassicyInput
								id="proxy_path"
								labelTitle="Path:"
								prefillValue={settingsForm.path}
								disabled={!settingsForm.enabled}
								onChangeFunc={(e) =>
									setSettingsForm((s) => ({
										...s,
										path: e.target.value,
									}))
								}
							/>
						</ClassicyControlGroup>
						{/* Commenting out the Archive section for now */}
						{/* <ClassicyControlGroup label="Archive">
							<ClassicyInput
								id="archive_time"
								labelTitle="Archive Time:"
								prefillValue={settingsForm.archiveTime}
								disabled={!settingsForm.enabled}
								onChangeFunc={(e) =>
									setSettingsForm((s) => ({
										...s,
										archiveTime: e.target.value,
									}))
								}
							/>
							<ClassicyInput
								id="proxy_prefix"
								labelTitle="Proxy Prefix:"
								prefillValue={settingsForm.proxyPrefix}
								disabled={!settingsForm.enabled}
								onChangeFunc={(e) =>
									setSettingsForm((s) => ({
										...s,
										proxyPrefix: e.target.value,
									}))
								}
							/>
						</ClassicyControlGroup> */}
						<div className="browserSettingsButtons">
							<ClassicyButton
								onClickFunc={() => setShowSettings(false)}
							>
								Cancel
							</ClassicyButton>
							<ClassicyButton isDefault={true} onClickFunc={saveSettings}>
								Save
							</ClassicyButton>
						</div>
					</div>
				</ClassicyWindow>
			)}
			{urlError && (
				<ClassicyWindow
					id={"browser_error"}
					title={"Error"}
					appId={appId}
					modal={true}
					type={"error"}
					closable={true}
					resizable={false}
					zoomable={false}
					scrollable={false}
					collapsable={false}
					initialPosition={[200, 200]}
					onCloseFunc={() => setUrlError(false)}
				>
					<p>Please enter a valid URL starting with http:// or https://</p>
					<ClassicyButton onClickFunc={() => setUrlError(false)}>
						OK
					</ClassicyButton>
				</ClassicyWindow>
			)}
			<ClassicyWindow
				id={"browser"}
				title={pageTitle || appName}
				icon={windowIcon}
				appId={appId}
				scrollable={false}
				initialSize={["50%", "50%"]}
				initialPosition={["left", "top"]}
				appMenu={appMenu}
				growable={true}
			>
				<div className="browser">
					<div className="browserBar browserToolbar" ref={refToolbar}>
						<div className="browserToolbarInner">
							<div className="browserToolbarControls">
								<div className="browserNavButtons">
									<ClassicyButton onClickFunc={goBack} disabled={!canGoBack}>
										<div
											className={`browserNavButtonContent browserHoverSwap${!canGoBack ? " browserNavButtonContentDisabled" : ""}`}
										>
											<img
												src={
													ClassicyIcons.applications.internetExplorer.backward
												}
												className="browserIconDefault"
												alt="Back"
											/>
											<img
												src={
													ClassicyIcons.applications.internetExplorer.backwardOn
												}
												className="browserIconHover"
												alt="Back Hover"
											/>
											{!isCompact && (
												<ClassicyControlLabel label="Back"></ClassicyControlLabel>
											)}
										</div>
									</ClassicyButton>
									<ClassicyButton
										buttonSize="medium"
										onClickFunc={goForward}
										disabled={!canGoForward}
									>
										<div
											className={`browserNavButtonContent browserHoverSwap${!canGoForward ? " browserNavButtonContentDisabled" : ""}`}
										>
											<img
												src={
													ClassicyIcons.applications.internetExplorer.forward
												}
												className="browserIconDefault"
												alt="Forward"
											/>
											<img
												src={
													ClassicyIcons.applications.internetExplorer.forwardOn
												}
												className="browserIconHover"
												alt="Forward Hover"
											/>
											{!isCompact && (
												<ClassicyControlLabel label="Forward"></ClassicyControlLabel>
											)}
										</div>
									</ClassicyButton>
								</div>
								<div className="browserAddressBar">
									{!isCompact && <ClassicyControlLabel label="Address:" />}
									<ClassicyInput
										id={"browserAddress"}
										prefillValue={addressBarValue}
										onChangeFunc={(e) => setAddressBarValue(e.target.value)}
										backgroundColor="white"
										onEnterFunc={goTo}
									></ClassicyInput>
								</div>
								<ClassicyButton onClickFunc={() => goTo(undefined)}>
									<div className="browserNavButtonContent browserHoverSwap">
										<img
											src={ClassicyIcons.applications.internetExplorer.refresh}
											className="browserIconDefault"
											alt="Go"
										/>
										<img
											src={
												ClassicyIcons.applications.internetExplorer.refreshOn
											}
											className="browserIconHover"
											alt="Go Hover"
										/>
										{!isCompact && (
											<ClassicyControlLabel label="Go"></ClassicyControlLabel>
										)}
									</div>
								</ClassicyButton>
								<ClassicyButton onClickFunc={() => goTo(homePage.url)}>
									<div className="browserNavButtonContent browserHoverSwap">
										<img
											src={ClassicyIcons.applications.internetExplorer.documentHome}
											className="browserIconDefault"
											alt={homePage.label}
										/>
										<img
											src={ClassicyIcons.applications.internetExplorer.documentHome}
											className="browserIconHover"
											alt={`${homePage.label} Hover`}
										/>
										{!isCompact && (
											<ClassicyControlLabel label="Home"></ClassicyControlLabel>
										)}
									</div>
								</ClassicyButton>
							</div>
						</div>
						<img
							src={
								isLoading
									? `${import.meta.env.BASE_URL}img/throbber.gif`
									: `${import.meta.env.BASE_URL}img/throbber-static.gif`
							}
							className="browserLoaderIcon"
							alt="Loader"
						/>
					</div>
					{showFavoritesBar && (
						<div className="browserBar browserFavoritesBar">
							<ClassicyControlLabel label="Favorites: "></ClassicyControlLabel>
							{favorites.map((fav) => (
								<ClassicyButton
									key={fav.id}
									onClickFunc={() => goTo(fav.url)}
									buttonSize="small"
								>
									<div className="browserNavButtonContent">
										<img src={fav.icon} alt={fav.title} />
										{!isCompact && (
											<ClassicyControlLabel
												label={fav.title}
												labelSize="small"
											></ClassicyControlLabel>
										)}
									</div>
								</ClassicyButton>
							))}
						</div>
					)}
					<div className="browserContents">
						<ShadowContent
							html={htmlContent}
							onLinkClick={handleContentClick}
							isVisited={isVisited}
						/>
					</div>
					<div className="browserStatusBar">{statusText}</div>
				</div>
			</ClassicyWindow>
			{aboutWindow}
		</ClassicyApp>
	);
};
